import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { createSchool, fetchMySchools } from "../../lib/schools";
import { formatApiError } from "../../lib/api";
import { normalizeExternalSchoolUrl } from "../../lib/hostSchool";
import FormLabel from "../FormLabel";
import FormFieldError, { fieldClass } from "../FormFieldError";
import CityAutocomplete from "../CityAutocomplete";
import { FieldHint, ReadablePillToggle, SectionTitle } from "../createEvent/CreateEventFormUi";

/**
 * Host-school promotion setup for create/edit event.
 * Default: promote on. Destinations: managed school, create school, or external name+URL.
 */
export default function HostSchoolPromotionField({
  value,
  onChange,
  error,
  disabled = false,
}) {
  const {
    school_id = null,
    school_external_name = "",
    school_external_url = "",
    host_school_opt_out = false,
  } = value || {};

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("internal"); // internal | create | external
  const [creating, setCreating] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: "", city: "", city_location: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMySchools("ADMIN");
        if (cancelled) return;
        const managing = data.managing || [];
        setSchools(managing);
        if (school_external_name || school_external_url) {
          setMode("external");
        } else if (managing.length === 0) {
          setMode("create");
        } else {
          setMode("internal");
          if (!host_school_opt_out && !school_id && managing.length === 1) {
            onChange({
              school_id: managing[0].id,
              school_external_name: "",
              school_external_url: "",
              host_school_opt_out: false,
            });
          }
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentional: load managed schools once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patch(partial) {
    onChange({
      school_id,
      school_external_name,
      school_external_url,
      host_school_opt_out,
      ...partial,
    });
  }

  function setOptOut(checked) {
    if (checked) {
      patch({
        host_school_opt_out: true,
        school_id: null,
        school_external_name: "",
        school_external_url: "",
      });
      return;
    }
    patch({ host_school_opt_out: false });
  }

  function selectMode(next) {
    setMode(next);
    if (next === "internal") {
      patch({
        host_school_opt_out: false,
        school_external_name: "",
        school_external_url: "",
        school_id: schools.length === 1 ? schools[0].id : school_id,
      });
    } else if (next === "external") {
      patch({
        host_school_opt_out: false,
        school_id: null,
      });
    } else {
      patch({
        host_school_opt_out: false,
        school_id: null,
        school_external_name: "",
        school_external_url: "",
      });
    }
  }

  async function handleCreateSchool() {
    const name = newSchool.name.trim();
    if (!name) {
      toast.error("Enter a school name.");
      return;
    }
    if (!newSchool.city?.trim() || !newSchool.city_location) {
      toast.error("Select the school city from the suggestions.");
      return;
    }
    setCreating(true);
    try {
      const school = await createSchool({
        name,
        city: newSchool.city.trim(),
      });
      setSchools((prev) => [...prev, school]);
      setMode("internal");
      patch({
        host_school_opt_out: false,
        school_id: school.id,
        school_external_name: "",
        school_external_url: "",
      });
      toast.success("School created and linked to this event.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true" data-testid="host-school-promo-loading">
        <SectionTitle>Host school</SectionTitle>
        <div className="h-4 w-56 rounded-md2 bg-black/[0.06] animate-pulse motion-reduce:animate-none" />
        <div className="h-11 w-full rounded-md2 bg-black/[0.06] animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }

  const inputsDisabled = disabled || host_school_opt_out;

  return (
    <div className="space-y-3" data-testid="host-school-promotion-field" id="host-school-promotion-field">
      <div>
        <SectionTitle>Host school</SectionTitle>
        <FieldHint className="mt-1">
          Guests will see your school under the event header — a big reason hosts use rsvp.dance.
        </FieldHint>
      </div>

      <label className="flex items-start gap-3 cursor-pointer py-1">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-black/20"
          checked={Boolean(host_school_opt_out)}
          disabled={disabled}
          onChange={(e) => setOptOut(e.target.checked)}
          data-testid="host-school-opt-out"
        />
        <span className="font-ui text-[14px] font-medium text-ink leading-snug">
          Don’t promote a school for this event
        </span>
      </label>

      {!host_school_opt_out && (
        <>
          <div className="flex flex-wrap gap-2" role="group" aria-label="School destination">
            {schools.length > 0 && (
              <ReadablePillToggle
                active={mode === "internal"}
                disabled={inputsDisabled}
                onClick={() => selectMode("internal")}
                data-testid="host-school-mode-internal"
              >
                My school
              </ReadablePillToggle>
            )}
            <ReadablePillToggle
              active={mode === "create"}
              disabled={inputsDisabled}
              onClick={() => selectMode("create")}
              data-testid="host-school-mode-create"
            >
              Create school
            </ReadablePillToggle>
            <ReadablePillToggle
              active={mode === "external"}
              disabled={inputsDisabled}
              onClick={() => selectMode("external")}
              data-testid="host-school-mode-external"
            >
              External link
            </ReadablePillToggle>
          </div>

          {mode === "internal" && schools.length > 0 && (
            <div>
              <FormLabel as="label" htmlFor="host-school-select" className="block mb-2">
                School
              </FormLabel>
              <select
                id="host-school-select"
                className={fieldClass(error)}
                value={school_id || ""}
                disabled={inputsDisabled}
                onChange={(e) =>
                  patch({
                    school_id: e.target.value || null,
                    school_external_name: "",
                    school_external_url: "",
                    host_school_opt_out: false,
                  })
                }
                data-testid="host-school-select"
              >
                {schools.length > 1 && (
                  <option value="" disabled>
                    Select a school
                  </option>
                )}
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === "create" && (
            <div className="space-y-3 rounded-card border border-black/[0.08] bg-cream/40 p-3">
              <label className="block">
                <FormLabel className="block mb-2">School name</FormLabel>
                <input
                  className="field"
                  value={newSchool.name}
                  disabled={inputsDisabled || creating}
                  onChange={(e) => setNewSchool((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Sala Caracas Dance Academy"
                  data-testid="host-school-create-name"
                />
              </label>
              <div className="block">
                <FormLabel className="block mb-2">School city</FormLabel>
                <CityAutocomplete
                  value={newSchool.city}
                  onChange={(v) =>
                    setNewSchool((s) => ({
                      ...s,
                      city: v,
                      city_location: v !== s.city ? null : s.city_location,
                    }))
                  }
                  onResolvedChange={(loc) =>
                    setNewSchool((s) => ({
                      ...s,
                      city: loc?.name || s.city,
                      city_location: loc,
                    }))
                  }
                  placeholder="Berlin"
                  testId="host-school-create-city"
                />
              </div>
              <button
                type="button"
                className="w-full sm:w-auto min-h-[44px] rounded-btn bg-ink text-cream px-4 py-2.5 font-ui font-medium text-[14px] disabled:opacity-50"
                disabled={inputsDisabled || creating}
                onClick={handleCreateSchool}
                data-testid="host-school-create-submit"
              >
                {creating ? "Creating…" : "Create & link school"}
              </button>
            </div>
          )}

          {mode === "external" && (
            <div className="space-y-3">
              <label className="block">
                <FormLabel className="block mb-2">School name</FormLabel>
                <input
                  className={fieldClass(error)}
                  value={school_external_name}
                  disabled={inputsDisabled}
                  onChange={(e) =>
                    patch({
                      school_external_name: e.target.value,
                      school_id: null,
                      host_school_opt_out: false,
                    })
                  }
                  placeholder="Sala Caracas"
                  data-testid="host-school-external-name"
                />
              </label>
              <label className="block">
                <FormLabel className="block mb-2">School website</FormLabel>
                <input
                  className={fieldClass(error)}
                  value={school_external_url}
                  disabled={inputsDisabled}
                  onChange={(e) =>
                    patch({
                      school_external_url: e.target.value,
                      school_id: null,
                      host_school_opt_out: false,
                    })
                  }
                  onBlur={(e) => {
                    const normalized = normalizeExternalSchoolUrl(e.target.value);
                    if (normalized && normalized !== e.target.value) {
                      patch({
                        school_external_url: normalized,
                        school_id: null,
                        host_school_opt_out: false,
                      });
                    }
                  }}
                  placeholder="https://salacaracas.com"
                  inputMode="url"
                  data-testid="host-school-external-url"
                />
              </label>
            </div>
          )}
        </>
      )}

      {error ? <FormFieldError id="host-school-error">{error}</FormFieldError> : null}
    </div>
  );
}
